<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import clubApplicationService from '../services/clubApplication.service'
import { useToastStore } from '../stores/toast'
import type { SignupLink } from '../types/clubApplication.types'

const props = defineProps<{
  show: boolean
  clubUuid: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()
const toastStore = useToastStore()

const link = ref<SignupLink | null>(null)
const loading = ref(false)
const downloading = ref(false)
const saving = ref(false)
const confirmRegenerate = ref(false)

const fetchLink = async () => {
  if (!props.clubUuid) return
  loading.value = true
  try {
    link.value = await clubApplicationService.getSignupLink(props.clubUuid)
  } catch (err: any) {
    console.error('Fetch signup link error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorFetchingLink'))
  } finally {
    loading.value = false
  }
}

watch(() => props.show, (visible) => {
  if (visible) {
    confirmRegenerate.value = false
    fetchLink()
  }
})

const close = () => {
  if (!saving.value) emit('close')
}

const copyLink = async () => {
  if (!link.value?.url) return
  try {
    await navigator.clipboard.writeText(link.value.url)
    toastStore.success(t('clubApplications.linkCopied'))
  } catch (err) {
    console.error('Copy error:', err)
    toastStore.error(t('dashboard.errorCopying'))
  }
}

const downloadQr = async () => {
  downloading.value = true
  try {
    const { blob, fileName } = await clubApplicationService.downloadQrPdf(props.clubUuid)
    const blobUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(blobUrl)
  } catch (err) {
    console.error('Download QR error:', err)
    toastStore.error(t('clubApplications.errorDownloadingQr'))
  } finally {
    downloading.value = false
  }
}

const toggleEnabled = async () => {
  if (!link.value) return
  saving.value = true
  try {
    const updated = await clubApplicationService.updateSignupLink(props.clubUuid, !link.value.enabled)
    link.value = updated
    if (updated.message) toastStore.success(updated.message)
  } catch (err: any) {
    console.error('Update signup link error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorSavingLink'))
  } finally {
    saving.value = false
  }
}

const regenerate = async () => {
  saving.value = true
  try {
    const updated = await clubApplicationService.regenerateSignupLink(props.clubUuid)
    link.value = updated
    confirmRegenerate.value = false
    if (updated.message) toastStore.success(updated.message)
  } catch (err: any) {
    console.error('Regenerate signup link error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorSavingLink'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="close"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-lg w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-2">{{ t('clubApplications.signupLinkTitle') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('clubApplications.signupLinkDescription') }}</p>

        <div v-if="loading" class="flex justify-center py-6">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <template v-else-if="link">
          <div
            v-if="!link.enabled"
            class="mb-4 p-3 bg-amber-50 border-2 border-amber-300 rounded text-sm text-amber-800"
          >
            {{ t('clubApplications.signupDisabledInfo') }}
          </div>

          <input
            type="text"
            :value="link.url"
            disabled
            class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-100 text-gray-700"
          >

          <div class="mt-3 flex flex-wrap gap-2">
            <button
              @click="copyLink"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              {{ t('clubApplications.copyLink') }}
            </button>
            <button
              @click="downloadQr"
              :disabled="downloading"
              class="px-4 py-2 bg-white text-sm font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
              {{ downloading ? t('clubApplications.downloading') : t('clubApplications.downloadQr') }}
            </button>
          </div>

          <!-- Management (OWNER / SUPERVISOR) -->
          <div v-if="link.can_manage" class="mt-6 pt-4 border-t-2 border-gray-200 space-y-3">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                :checked="link.enabled"
                :disabled="saving"
                @change="toggleEnabled"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded"
              >
              <span class="text-sm font-medium text-gray-900">{{ t('clubApplications.signupEnabled') }}</span>
            </label>

            <div v-if="!confirmRegenerate">
              <button
                @click="confirmRegenerate = true"
                :disabled="saving"
                class="text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
              >
                {{ t('clubApplications.regenerateLink') }}
              </button>
            </div>
            <div v-else class="p-3 bg-red-50 border-2 border-red-300 rounded">
              <p class="text-sm text-red-800 mb-3">{{ t('clubApplications.regenerateConfirm') }}</p>
              <div class="flex gap-2 justify-end">
                <button
                  @click="confirmRegenerate = false"
                  :disabled="saving"
                  class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
                >
                  {{ t('common.cancel') }}
                </button>
                <button
                  @click="regenerate"
                  :disabled="saving"
                  class="px-3 py-1.5 bg-red-600 text-xs font-medium text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {{ t('clubApplications.regenerateLinkConfirmButton') }}
                </button>
              </div>
            </div>
          </div>
        </template>

        <div class="mt-6 flex justify-end">
          <button
            @click="close"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('clubApplications.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
